import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs'
import io, { Socket } from 'socket.io-client'

import { JwtService } from '@app/modules/auth/jwt.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { AbstractSocketDirective } from '@app/modules/client/map/shared/abstractSocket.directive'
import { TrackerHistoryPayload } from '@app/modules/client/map/shared/models/tracker-history-payload.model'
import { ApiService } from '@shared/services/api.service'
import { NetworkStatusService } from '@core/services/network-status.service'
import { MapVehiclesService } from '@app/modules/client/map/map-details/vehicles/map-vehicles.service'
import { MapListVehiclesService } from '@app/modules/client/map/map-details/vehicles/map-list-vehicles.service'
import { DestroyService } from '@core/services/destroy.service'
import { MapApiService } from '@app/modules/client/map/map-api.service'
import { MapService } from '@app/modules/client/map/map.service'
import { SocketState, SocketStatus } from './abstractSocket.directive'
import { MapRealtimeGateway } from '@core/services/realtime/map-realtime.gateway'

@Injectable()
export class SocketService extends AbstractSocketDirective implements MapRealtimeGateway {
  private vehicleIds: number[]
  private emitSubscribe: Subscription
  private coordinateObservable: Observable<TrackerHistoryPayload>

  public socket: Socket

  private coordinatesSubject = new Subject<any>()
  public coordinates$ = this.coordinatesSubject.asObservable()

  private trackerHistorySubject = new Subject<TrackerHistoryPayload>()
  public trackerHistory$ = this.trackerHistorySubject.asObservable()

  private socketStateSubject = new BehaviorSubject<SocketState>({
    status: SocketStatus.Disconnect,
  })

  public socketState$ = this.socketStateSubject.asObservable()
  private currentSocketState: SocketState = { status: SocketStatus.Disconnect }

  constructor(
    jwtService: JwtService,
    authService: AuthService,
    router: Router,
    apiService: ApiService,
    networkStatusService: NetworkStatusService,
    mapVehiclesService: MapVehiclesService,
    mapListVehiclesService: MapListVehiclesService,
    destroyService: DestroyService,
    mapApiService: MapApiService,
    mapService: MapService,
  ) {
    super(
      jwtService,
      authService,
      router,
      apiService,
      networkStatusService,
      mapVehiclesService,
      mapListVehiclesService,
      destroyService,
      mapApiService,
      mapService,
    )
  }

  public get socketStateSubject$(): BehaviorSubject<SocketState> {
    return this.socketStateSubject
  }

  public isConnected(state: SocketState): boolean {
    return state.status === SocketStatus.Connect
  }

  connectToNamespace(): Socket {
    if (!this.socket) {
      //console.log('[Map WS]', `${this.baseUrlWS}/coordinates`);
      this.socket = io(`${this.baseUrlWS}/coordinates`, this.defaultOptions)
      this.socket.on('error', (error: any) => {
        //console.error('Socket Error event:', error);
      })
      this.socket.on('connect_error', (error: any) => {
        //console.error('socket Connect error:', error);
      })
      this.socket.on('disconnect', (reason: any) => {
        //console.warn('Socket Disconnected:', reason);
        this.currentSocketState = { status: SocketStatus.Disconnect }
        this.socketStateSubject.next({ status: SocketStatus.Disconnect })
      })

      const lastReceivedTimeGlobal = { time: Date.now() }
      const lastReceivedByVehicle = new Map<number, number>()

      this.socket.on('coordinates', (payload: TrackerHistoryPayload) => {
        const now = Date.now()

        const diffGlobal = now - lastReceivedTimeGlobal.time
        //console.log(`${diffGlobal} ms since last coordinate message`);
        lastReceivedTimeGlobal.time = now

        const vehicleId = payload.vehicleId
        const lastTime = lastReceivedByVehicle.get(vehicleId)
        if (lastTime !== undefined) {
          const diff = now - lastTime
          //console.log(`Vehicle ${vehicleId} ${diff} ms since last update`);
        }
        lastReceivedByVehicle.set(vehicleId, now)

        this.coordinatesSubject.next(payload)
        this.trackerHistorySubject.next(payload)
      })

      this.socket.on('connect', () => {
        //console.log('Socket Connected');
        this.currentSocketState = { status: SocketStatus.Connect }
        this.socketStateSubject.next({ status: SocketStatus.Connect })
      })
    }
    return this.socket
  }

  public subscribe(vehicleIds: number[]): void {
    this.isSocketExist()
    this.vehicleIds = vehicleIds

    if (!this.socket) {
      return
    }

    if (!vehicleIds?.length) {
      return
    }

    const payload = { vehicleIds: vehicleIds }

    if (this.socket.connected) {
      this.socket.emit('subscribe', payload)
    } else {
      this.socket.once('connect', () => {
        this.socket.emit('subscribe', payload)
      })
    }
  }

  getCoordinates(): Observable<TrackerHistoryPayload> {
    if (!this.coordinateObservable) {
      this.coordinateObservable = new Observable((observer): void => {
        const handleCoordinates = (coordinates: TrackerHistoryPayload) => {
          //console.log('Socket Received coordinates:', coordinates);
          observer.next(coordinates)
        }

        const handleDisconnect = () => {
          //console.warn('Socket Socket disconnected — complete.');
          observer.complete()
          this.coordinateObservable = null
        }

        if (this.socket) {
          this.socket.on('coordinates', handleCoordinates)
          this.socket.on('disconnect', handleDisconnect)
        }
      })
    }

    return this.coordinateObservable
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners()

      this.socket.disconnect()
      this.socket = null

      this.coordinateObservable = null
      this.emitSubscribe?.unsubscribe()

      this.currentSocketState = { status: SocketStatus.Disconnect }
      this.socketStateSubject.next({ status: SocketStatus.Disconnect })
    }
  }
}
